import React, { useEffect, useState } from 'react';
import { getImage } from 'gatsby-plugin-image';
import PulseLoader from 'react-spinners/PulseLoader';
import PropTypes from 'prop-types';
import { snakeCase } from 'lodash';
import { DynamicGallery as DynamicGalleryCL } from '@comicrelief/component-library';

import renderRichText from '../RichText/RichText';
import imagePropType from '../../commonPropTypes/commonPropTypes';
import {
  OuterWrapper, ComponentWrapper, LoaderWrapper, NodeCaption
} from './DynamicGallery.styles';

import {
  galleryOrderMapping,
  paddingOptionMapping,
  galleryRatioMapping,
  galleryGridWidthMapping,
  yesOrNoToBoolean,
  galleryLoadingBehaviour
} from '../../utils/optionMapping';

const waitDuration = 1500;

const DynamicGallery = ({ data }) => {
  const {
    id,
    cardNodes,
    pageBackgroundColour,
    displayOrder,
    imageRatio,
    gridWidth,
    loadingBehaviour,
    useLightboxForImages,
    paddingAbove,
    paddingBelow,
    textColour = 'black'
  } = data;

  const [processedNodes, setProcessedNodes] = useState(null);
  const [showGallery, setShowGallery] = useState(false);

  // Map the human-readable labels to the actual values, setting 'undefined' where
  // necessary to allow the underlying component's default prop values to take over :
  const thisPaddingTop = paddingAbove ? paddingOptionMapping[paddingAbove] : undefined;
  const thisPaddingBottom = paddingBelow ? paddingOptionMapping[paddingBelow] : undefined;
  const thisOrderReversal = displayOrder ? galleryOrderMapping[displayOrder] : undefined;
  const thisRatio = imageRatio ? galleryRatioMapping[imageRatio] : undefined;
  const thisGridWidth = gridWidth ? galleryGridWidthMapping[gridWidth] : undefined;
  const thisLightbox = useLightboxForImages ? yesOrNoToBoolean[useLightboxForImages] : undefined;
  const thisLoading = loadingBehaviour ? galleryLoadingBehaviour[loadingBehaviour] : undefined;

  // Cast colours to the usual format:
  const thisPageBgColour = pageBackgroundColour ? snakeCase(pageBackgroundColour) : undefined;
  const thisTextColour = snakeCase(textColour);

  /**
   * Helper function to prepare nodes for Dynamic Gallery component
   * @param {object} nodes All nodes added to this gallery
   * @param {bool} reverse Whether or not the order should be reversed
   */
  const processNodes = (nodes, reverse) => {
    const payload = [];
    const orderedNodes = reverse ? nodes.reverse() : nodes;

    orderedNodes.forEach((node) => {
      let thisImage = getImage(node?.image);
      thisImage = thisImage?.images?.fallback?.src;

      // Belt and braces check, just to be safe that Contentful hasn't dropped the ball:
      if (thisImage) {
        const textCentered = node?.centeredText ? 'center' : undefined;

        payload.push({
          image: thisImage,
          centeredText: node?.centeredText,
          // As the Lightbox bg is always white, just hardcode
          // it to set the textColour within renderRichText
          // TODO: need to refactor how these params are passed to renderRichText
          lightboxBody: node?.body
            && renderRichText(node?.body, 'white', null, null, null, null, textCentered),
          // And directly set the colour via the Text prop:
          lightboxCaption: node?.caption
            && <NodeCaption textAlign={textCentered} color="black">{node.caption}</NodeCaption>,
          // However, the 'grid' versions needs to work dynamically:
          gridBody: node?.body
          && renderRichText(node?.body, thisPageBgColour, null, null, null, null, textCentered),
          gridCaption: node?.caption
          && (
          <NodeCaption
            textAlign={textCentered}
            color={thisTextColour}
          >
            {node.caption}
          </NodeCaption>
          )
        });
      }
    });

    setProcessedNodes(payload);

    // Gives the busy-but-hiddem component a second to catch up
    // before showing, to allow for a smoother transition via CSS:
    setTimeout(() => {
      setShowGallery(true);
    }, waitDuration);
  };

  // Process all of the node content on initial load, before rendering anything:
  useEffect(() => {
    processNodes(cardNodes, thisOrderReversal);
  }, []);

  return (
    <OuterWrapper key={id} pageBackgroundColour={thisPageBgColour}>

      {/* Load the Gallery once we've got processed content, keeping it hidden briefly */}
      { processedNodes
        && (
          <ComponentWrapper show={showGallery}>
            <DynamicGalleryCL
              nodes={processedNodes}
              textColour={thisTextColour}
              pageBackgroundColour={thisPageBgColour}
              paddingTop={thisPaddingTop}
              paddingBottom={thisPaddingBottom}
              imageRatio={thisRatio}
              gridWidth={thisGridWidth}
              useLightbox={thisLightbox}
              loadingBehaviour={thisLoading}
            />
          </ComponentWrapper>
        )}

      {!showGallery && (
      <LoaderWrapper>
        <PulseLoader height={16} width={2} color={thisTextColour} loading />
      </LoaderWrapper>
      )}

    </OuterWrapper>
  );
};

DynamicGallery.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    pageBackgroundColour: PropTypes.string,
    textColour: PropTypes.string,
    gridWidth: PropTypes.string,
    loadingBehaviour: PropTypes.string,
    displayOrder: PropTypes.string,
    imageRatio: PropTypes.string,
    useLightboxForImages: PropTypes.string,
    paddingAbove: PropTypes.string,
    paddingBelow: PropTypes.string,
    cardNodes: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        image: imagePropType,
        body: PropTypes.shape({
          raw: PropTypes.string
        }),
        caption: PropTypes.string,
        centeredText: PropTypes.bool
      })
    )
  }).isRequired
};

export default DynamicGallery;
